import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_SALES_TAX,
  LINE_ITEM_SUCCESS_FEE_TAX,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * The base amount the provider commission is charged on: the item (and shipping)
 * total, before the buyer's sales tax. This is the sum of the positive,
 * non-commission, non-tax line items.
 */
const commissionBase = lineItems =>
  lineItems
    .filter(
      li =>
        !li.reversal &&
        li.code !== LINE_ITEM_PROVIDER_COMMISSION &&
        li.code !== LINE_ITEM_SALES_TAX &&
        li.code !== LINE_ITEM_SUCCESS_FEE_TAX &&
        (li.lineTotal?.amount ?? 0) > 0
    )
    .reduce((sum, li) => sum + li.lineTotal.amount, 0);

/**
 * Provider-only. Opens the marketplace-fee section with the base item price the
 * commission percentage is applied to, so the seller can see the fee is charged
 * on the pre-tax item amount.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - Provider line items.
 * @param {boolean} props.isProvider
 * @param {string} props.currency - Marketplace currency.
 * @param {intlShape} props.intl
 * @returns {JSX.Element|null}
 */
const LineItemCommissionBaseMaybe = props => {
  const { lineItems, isProvider, currency, intl } = props;
  const hasCommission = lineItems.some(
    li => li.code === LINE_ITEM_PROVIDER_COMMISSION && !li.reversal
  );
  if (!isProvider || !hasCommission) return null;

  const baseAmount = commissionBase(lineItems);
  if (baseAmount <= 0) return null;
  const baseCurrency = lineItems.find(li => li.lineTotal?.currency)?.lineTotal?.currency || currency;

  return (
    <>
      <hr className={css.sectionDivider} />
      <div className={css.lineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.feeBaseItemPrice" />
        </span>
        <span className={css.itemValue}>{formatMoney(intl, new Money(baseAmount, baseCurrency))}</span>
      </div>
    </>
  );
};

export default LineItemCommissionBaseMaybe;
